/**
 * Regras compartilhadas pelo assistente de horários e pelo modal de um dia.
 *
 * Os dois mostram o mesmo dia em campos de Manhã e Tarde; se cada um calculasse a sua cópia,
 * o mesmo prestador apareceria diferente conforme o botão usado.
 */

const MEIO_DIA = '12:00';

interface BlocoComHorario {
  startTime: string;
}

export interface BlocosDoDia<T extends BlocoComHorario> {
  manha: T | undefined;
  tarde: T | undefined;
}

/**
 * Distribui os blocos de um dia nos campos Manhã e Tarde.
 *
 * Um bloco: começa antes das 12:00 = manhã; às 12:00 ou depois = tarde.
 * Dois blocos: a ordem manda (primeiro em Manhã, segundo em Tarde), mesmo que os dois caiam do
 * mesmo lado do meio-dia, para nenhum sumir da tela. Três ou mais: só os dois primeiros.
 */
export function classificarBlocosDoDia<T extends BlocoComHorario>(blocos: T[]): BlocosDoDia<T> {
  const ordenados = [...blocos].sort((a, b) => a.startTime.localeCompare(b.startTime));

  if (ordenados.length === 0) return { manha: undefined, tarde: undefined };
  if (ordenados.length === 1) {
    const unico = ordenados[0]!;
    return unico.startTime < MEIO_DIA
      ? { manha: unico, tarde: undefined }
      : { manha: undefined, tarde: unico };
  }
  return { manha: ordenados[0], tarde: ordenados[1] };
}

/** Capacidade do servidor para o campo: nula e 0 legado (que nunca foi válido) abrem vazios. */
export function capacidadeParaCampo(capacidade: number | null | undefined): string {
  return capacidade ? String(capacidade) : '';
}

/** Texto do campo para o servidor: vazio vira null (vale o padrão do prestador). */
export function campoParaCapacidade(texto: string): number | null {
  return texto ? Number(texto) : null;
}

export interface PeriodosDoDia {
  morningStart: string;
  morningEnd: string;
  afternoonStart: string;
  afternoonEnd: string;
}

/**
 * O primeiro período que tem só um dos dois horários, ou null se o dia está inteiro.
 * Devolve o texto pronto para o alerta, citando o dia.
 */
export function periodoPelaMetade(nomeDoDia: string, dia: PeriodosDoDia): string | null {
  if (dia.morningStart && !dia.morningEnd) return `${nomeDoDia}: informe o fim da manhã`;
  if (!dia.morningStart && dia.morningEnd) return `${nomeDoDia}: informe o início da manhã`;
  if (dia.afternoonStart && !dia.afternoonEnd) return `${nomeDoDia}: informe o fim da tarde`;
  if (!dia.afternoonStart && dia.afternoonEnd) return `${nomeDoDia}: informe o início da tarde`;
  return null;
}
